import { ReactNode, useMemo } from "react";
import { I18nextProvider } from "react-i18next";
import { usePageContext } from "vike-react/usePageContext";

import { DEFAULT_LANGUAGE, Locale } from "./constants";
import { i18n } from "./i18n";

/** Must match `defaultNS` in i18n.ts. */
const NAMESPACE = "common";

interface I18nProviderProps {
	children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
	const pageContext = usePageContext();
	const locale = (pageContext.locale as Locale | undefined) ?? DEFAULT_LANGUAGE;
	const translations = (pageContext as { translations?: Record<string, unknown> }).translations;

	/**
	 * A per-locale i18next instance seeded with this page's translation slice.
	 *
	 * Two things are deliberate here:
	 *
	 * 1. The resources come from `pageContext` rather than a static import. i18next
	 *    is initialised with no resources at all (see i18n.ts), so the browser only
	 *    ever holds the active language and the areas the current page uses instead
	 *    of both full catalogs (~250 KB).
	 *
	 * 2. The work happens during render, not in an effect. Effects do not run
	 *    during SSR or prerendering, so an effect-based version rendered every
	 *    localized page into static HTML in the default language — and on the
	 *    client it would arrive after hydration, making React swap the text.
	 */
	const instance = useMemo(() => {
		const target = i18n.language === locale ? i18n : i18n.cloneInstance({ lng: locale });
		if (translations) {
			// `deep: true`, `overwrite: false` — merge into whatever a previous page
			// already put there and never clobber it, so client-side navigation
			// accumulates areas instead of dropping the ones already loaded.
			target.addResourceBundle(locale, NAMESPACE, translations, true, false);
		}
		return target;
	}, [locale, translations]);

	// `<html lang>` is owned by pages/+lang.ts (vike-react's `lang` setting), which
	// emits the BCP 47 tag for Armenian, `hy`, which is now also its URL segment. Setting
	// it here as well would overwrite it with the raw segment on the client.
	return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
}
